import { useState } from 'react';
import { analyserSituation, formaterDate } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { Bloc, Tableau } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { DepotFichierTexte } from '../composants/DepotFichierTexte';
import { Note } from '../composants/Note';

interface Anomalie {
  ligne: number | null;
  code: string;
  message: string;
  contenu?: string | null;
}

interface Resultat {
  rejoue: boolean;
  mandatsIntegres: number;
  mandatsRejetes: number;
  anomalies: Anomalie[];
  cascade: { dates: { date: string; mandats: number }[]; cascade: number } | null;
}

interface LigneHistorique {
  id: string;
  nomFichier: string;
  dateReference: string;
  nbLignes: number;
  nbLignesOk: number;
  nbAnomalies: number;
  statut: string;
  importeLe: string;
}

/**
 * Une anomalie n'est pas toujours un refus. Celles-ci constatent un fait que la
 * SDG ne maîtrise pas — une ligne BDT choisie par la salle des marchés, un
 * écart de réconciliation à élucider : elles appellent un arbitrage, pas un
 * rejet.
 */
const INFORMATIVES = new Set([
  'INSTRUMENT_A_COMPLETER',
  'TRANCHE_NON_CONFORME',
  'RECONCILIATION_ESPECES',
]);

const EXEMPLE = `#BLOC SOLDES
reference_mandat;date_situation;solde_espece
MDT-2026-000123;2026-09-01;48250.00

#BLOC POSITIONS
reference_mandat;date_situation;type;isin;code;libelle;quantite;pru;montant_investi
MDT-2026-000123;2026-09-01;ACTION;MA0000012445;IAM;Itissalat Al-Maghrib;4000;108.562500;434250.00

#BLOC MOUVEMENTS
reference_mandat;reference_mouvement;date_operation;date_valeur;type;sens;montant;libelle
MDT-2026-000123;BO-2026-0099872;2026-08-29;2026-08-31;VERSEMENT;CREDIT;300000.00;Virement client`;

export function ImportSituation() {
  const [texte, setTexte] = useState('');
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const historique = useRessource<LigneHistorique[]>('/situations');
  const analyse = texte.trim() ? analyserSituation(texte) : null;

  async function importer() {
    setErreur(null);
    setResultat(null);
    setOccupe(true);
    try {
      const lu = analyserSituation(texte);
      if (lu.manques.length > 0) throw new Error(lu.manques.join(' '));
      if (!lu.dateSituation) throw new Error('Aucune date de situation lisible.');

      setResultat(
        await api.post<Resultat>('/situations/import', {
          dateSituation: lu.dateSituation,
          nomFichier: nomFichier ?? `situation-${lu.dateSituation}.csv`,
          contenu: texte,
          soldes: lu.soldes,
          positions: lu.positions,
          mouvements: lu.mouvements,
        }),
      );
      historique.recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : String(e));
    } finally {
      setOccupe(false);
    }
  }

  const bloquantes = (resultat?.anomalies ?? []).filter((a) => !INFORMATIVES.has(a.code));
  const signalements = (resultat?.anomalies ?? []).filter((a) => INFORMATIVES.has(a.code));

  return (
    <>
      <p className="entete__fil">Valorisation</p>
      <div className="entete">
        <h1>Situation reçue</h1>
        <span className="entete__ref">back-office tiers</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Import refusé">
          {erreur}
        </BandeauAlerte>
      )}

      <Bloc titre="Fichier de situation" meta="UTF-8 ; séparateur « ; » ; dates AAAA-MM-JJ">
        <Note
          tete
          resume="Trois blocs : soldes espèces, positions au prix de revient, journal des mouvements."
        >
          Un solde par mandat, même sans position ; le journal couvre la période écoulée depuis la
          situation précédente. Intégration atomique par mandat ; le rejeu du même fichier ne
          réécrit rien.
        </Note>
        <DepotFichierTexte
          nomFichier={nomFichier}
          onFichier={(contenu, nom) => {
            setTexte(contenu);
            setNomFichier(nom);
            setResultat(null);
            setErreur(null);
          }}
          onRetirer={() => {
            setTexte('');
            setNomFichier(null);
          }}
          aide="Le fichier reçu du back-office, tel quel. Son contenu s’affiche ci-dessous et reste modifiable avant envoi."
          desactive={occupe}
        />

        <label className="surtitre" htmlFor="contenu">
          Contenu du fichier
        </label>
        <textarea
          id="contenu"
          value={texte}
          onChange={(e) => {
            setTexte(e.target.value);
            // Retouché à la main, ce n'est plus le fichier reçu : on cesse
            // d'en porter le nom, que la piste d'audit conserverait à tort.
            setNomFichier(null);
          }}
          placeholder={EXEMPLE}
          rows={14}
          className="zone-texte"
        />

        {analyse && (
          <p className="consigne consigne--pied">
            {analyse.dateSituation ? `Situation au ${formaterDate(analyse.dateSituation)} — ` : ''}
            {analyse.soldes.length} mandat(s), {analyse.positions.length} position(s),{' '}
            {analyse.mouvements.length} mouvement(s).
          </p>
        )}

        {analyse && analyse.manques.length > 0 && (
          <BandeauAlerte nature="gestion" titre="Le fichier ne peut pas être envoyé">
            <ul className="liste-compacte">
              {analyse.manques.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </BandeauAlerte>
        )}

        <div className="espace-avant">
          <button
            className="bouton"
            disabled={occupe || !analyse || analyse.manques.length > 0}
            onClick={() => void importer()}
          >
            {occupe ? 'En cours…' : 'Intégrer la situation'}
          </button>
        </div>
      </Bloc>

      {resultat && (
        <Bloc
          titre="Compte rendu"
          meta={
            resultat.rejoue
              ? 'fichier déjà intégré'
              : `${resultat.mandatsIntegres} intégré(s), ${resultat.mandatsRejetes} rejeté(s)`
          }
        >
          {resultat.rejoue && (
            <BandeauAlerte nature="information" titre="Fichier déjà intégré">
              Ce fichier a déjà été reçu : rien n’a été réécrit.
            </BandeauAlerte>
          )}

          {resultat.cascade && resultat.cascade.cascade > 0 && (
            <BandeauAlerte nature="information" titre="Revalorisation en cascade">
              {resultat.cascade.dates.length} date(s) revalorisée(s), dont{' '}
              {resultat.cascade.cascade} postérieure(s) : le journal portait des mouvements
              antérieurs à la situation, qui déplacent les soldes de toutes les dates suivantes.
            </BandeauAlerte>
          )}

          {bloquantes.length > 0 && (
            <BandeauAlerte nature="refus" titre={`${bloquantes.length} mandat(s) rejeté(s)`}>
              Ces situations n’ont pas été intégrées. Le mandat reste sur son état précédent.
            </BandeauAlerte>
          )}

          {resultat.anomalies.length === 0 ? (
            <p className="consigne">Aucune anomalie.</p>
          ) : (
            <Tableau
              colonnes={[
                {
                  cle: 'ligne',
                  entete: 'Ligne',
                  num: true,
                  rendu: (a: Anomalie) => a.ligne ?? '—',
                },
                { cle: 'code', entete: 'Code', rendu: (a: Anomalie) => a.code },
                { cle: 'message', entete: 'Constat', rendu: (a: Anomalie) => a.message },
              ]}
              lignes={[...bloquantes, ...signalements]}
              cle={(_, i) => String(i)}
              vide="Aucune anomalie."
            />
          )}
        </Bloc>
      )}

      <Bloc titre="Situations reçues" meta="les vingt dernières">
        <Tableau
          colonnes={[
            { cle: 'fichier', entete: 'Fichier', rendu: (l: LigneHistorique) => l.nomFichier },
            {
              cle: 'date',
              entete: 'Situation au',
              rendu: (l: LigneHistorique) => formaterDate(l.dateReference),
            },
            {
              cle: 'lignes',
              entete: 'Lignes',
              num: true,
              rendu: (l: LigneHistorique) => l.nbLignes,
            },
            {
              cle: 'ok',
              entete: 'Mandats intégrés',
              num: true,
              rendu: (l: LigneHistorique) => l.nbLignesOk,
            },
            {
              cle: 'anomalies',
              entete: 'Anomalies',
              num: true,
              rendu: (l: LigneHistorique) => l.nbAnomalies || '—',
            },
            { cle: 'statut', entete: 'État', rendu: (l: LigneHistorique) => l.statut },
          ]}
          lignes={historique.donnees ?? []}
          cle={(l) => l.id}
          vide="Aucune situation reçue à ce jour."
        />
      </Bloc>
    </>
  );
}
